
import { roles } from '../models/roles';
import { Role } from '../types/Role';
import { BaseRepository } from '../../../classes/BaseRepository';
import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { eq, and, inArray } from 'drizzle-orm';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';

/**
 * @group Role
 * @summary Role repository
 * 
 * <blockquote>
 * Provides data access methods for role management, including CRUD operations and role-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const roleRepository = new RoleRepository();
 * const role = await roleRepository.getRoleById('123');
 * ```
 * 
 */
export class RoleRepository extends BaseRepository<Role> {
  protected table = roles;

  /**
   * Gets or creates a default role for new users
   * 
   * @param tenantId - The tenant ID to get/create the role for
   * @param roleName - The name of the role (defaults to 'User')
   * @returns AsyncResult containing the role or error
   */
  async getOrCreateDefaultRole(tenantId: string, roleName: string = 'User'): AsyncResult<Role, AppError> {
    if (!tenantId) {
      return err(convertToAppError(
        'Tenant ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    // First try to find existing role
    const existingRoleResult = await this.findByFields({
      name: roleName,
      tenant_id: tenantId
    });

    if (existingRoleResult.isErr) {
      return err(existingRoleResult.getFail());
    }

    const existingRoles = existingRoleResult.getSuccess();

    if (existingRoles.length > 0) {
      return ok(existingRoles[0]);
    }

    // Create new role if it doesn't exist
    const createRoleResult = await this.create({
      name: roleName,
      tenant_id: tenantId
    });

    if (createRoleResult.isErr) {
      return err(createRoleResult.getFail());
    }

    return ok(createRoleResult.getSuccess());
  }

  /**
   * Gets a role by name and tenant ID
   * 
   * @param name - The role name
   * @param tenantId - The tenant ID
   * @returns AsyncResult containing the role or error
   */
  async getRoleByNameAndTenant(name: string, tenantId: string): AsyncResult<Role | null, AppError> {
    if (!name || !tenantId) {
      return err(convertToAppError(
        'Role name and tenant ID are required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select()
            .from(this.table)
            .where(and(eq(this.table.name, name), eq(this.table.tenant_id, tenantId)))
            .limit(1)
        );

        return result.match({
          ok: (rows) => ok(rows.length > 0 ? rows[0] : null),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get roles for specific tenant IDs
   * 
   * @param tenantIds - Array of tenant IDs to filter roles by
   * @returns AsyncResult containing array of roles for the specified tenants, or error
   * 
   * @example
   * ```typescript
   * const rolesResult = await repository.getRolesByTenantIds(['tenant-123', 'tenant-456']);
   * if (rolesResult.isOk()) {
   *   const roles = rolesResult.value;
   *   console.log(`Found ${roles.length} roles in tenant scope`);
   * }
   * ```
   */
  async getRolesByTenantIds(tenantIds: string[]): AsyncResult<Role[], AppError> {
    if (!tenantIds || tenantIds.length === 0) {
      return ok([]);
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select()
            .from(this.table)
            .where(inArray(this.table.tenant_id, tenantIds))
        );

        return queryResult.match({
          ok: (roleRows) => ok(roleRows),
          err: (error) =>
            err({
              errorMessage: error instanceof Error ? error.message : String(error),
            }),
        });
      },
      err: async (error) => err(error),
    });
  }
} 